import React, { useState } from 'react';

import ContentBlock from '@components/layout/contentBlock/ContentBlock';
import ContentItem from '@components/layout/contentBlock/ContentItem';
import ContentRow from '@components/layout/contentBlock/ContentRow';
import LinkButton from '@components/button/LinkButton';
import ButtonRow from 'lib/src/components/button/ButtonRow';
import Title from 'lib/src/components/typography/Title';
import ActionButton from 'lib/src/components/button/ActionButton';
import { useParams } from 'react-router';
import useFetchSingleChallenge from './_hooks/useFetchSingleChallenge';
import DataCheck from 'lib/src/components/common/DataCheck';
import { getS3Filename, isEmpty } from 'lib/src/utils/generic';
import dayjs from 'dayjs';
import EditChallengeLeadModal from './EditChallengeLeadModal';
import EditChallengeScoreWeightingModal from './EditChallengeScoreWeightingModal';
import Breadcrumb from '@components/breadcrumb/Breadcrumb';
import useFetchChallengeGroup from '../hooks/useFetchChallengeGroup';
import { getDecodedJwtToken, getJwtToken } from 'lib/src/utils/jwt';

import { challengeGroupsChallengeBreadcrumb } from '@constants/breadcrumbItems';
import ChallengeSubmissionsTable from './ChallengeSubmissionsTable';
import MarkToBeAssessedModal from './MarkToBeAssessedModal';
import MarkToBeCompletedModal from './MarkToBeCompletedModal';
import config from '../../../config/index';
import useCopyToClipboard from 'lib/src/hooks/useCopyToClipboard';
import { DescriptionSectionTypes } from '../../../types/shared/Challenge';
import { descriptionComponents } from '@constants/lookups';
import { challengeStageValues } from '../../../types/shared/Challenge';
import { userRoles } from 'lib/src/shared/enums/dropdownEnums';
import useHasChallengeCrudPermissions from '../../../utils/hooks/useHasChallengeCrudPermissions';
import ResetAssessmentStatusModal from './ResetAssessmentStatusModal';
import useFetchSubmissions from '@pages/assessmentChallenges/submissions/hooks/useFetchSubmissions';
import PublishChallengeModal from './PublishChallengeModal';

const ChallengeGroupChallengeComponent: React.FC = () => {
    const { id: groupId, challengeid: id } = useParams<{ id: string; challengeid: string }>();
    const jwt = getDecodedJwtToken();
    const userID = jwt?.id;
    const isSuperAdmin = jwt?.roles.includes(userRoles.superAdmin);

    const {
        challenge,
        isFetching,
        fetchError,
        workshopAdmins,
        assessors,
        pitchPresentationAdmins,
    } = useFetchSingleChallenge(+id);
    const { challengeGroup } = useFetchChallengeGroup(+groupId);

    const {
        submissions,
        isFetching: isFetchingSubmissions,
        fetchError: fetchSubmissionsError,
    } = useFetchSubmissions(+id);

    const leadUser = challenge.challengeLead;

    const { copyToClipboard, copySuccess, copyFailure } = useCopyToClipboard();
    const {
        copyToClipboard: copyPreviewToCliboard,
        copySuccess: copyPreviewSuccess,
        copyFailure: copyPreviewFailure,
    } = useCopyToClipboard();

    const [showEditScoreWeightingModal, setShowEditScoreWeightingModal] = useState<boolean>(false);
    const [showEditLeadModal, setShowEditLeadModal] = useState<boolean>(false);
    const [showMarkToBeAssessedModal, setShowMarkToBeAssessedModal] = useState<boolean>(false);
    const [showMarkToBeCompletedModal, setShowMarkToBeCompletedModal] = useState<boolean>(false);
    const [showResetAssessmentStatusModal, setShowResetAssessmentStatusModal] = useState<boolean>(
        false,
    );
    const [showPublishChallengeModal, setShowPublishChallengeModal] = useState<boolean>(false);
    const [showUnPublishChallengeModal, setShowUnPublishChallengeModal] = useState<boolean>(false);

    const encodedJwt = getJwtToken();

    // subtracting 1 second to ensure the button is updated after publishing
    const isChallengeStarted =
        challenge.publishDate &&
        dayjs(challenge.publishDate).subtract(1, 'second').isBefore(dayjs());

    const isChallengeEnded = dayjs(challenge?.endDate)
        .add(challenge?.gracePeriod, 'day')
        .isBefore(dayjs());

    const shouldShowMarkAsAssessmentComplete =
        isChallengeEnded &&
        !!challenge.readyForAssessmentsSetOn &&
        !challenge.assessmentsCompleteSetOn;
    const shouldShowMarkAsReadyForAssessment =
        isChallengeEnded && !challenge.readyForAssessmentsSetOn;

    challengeGroupsChallengeBreadcrumb[2].name = challengeGroup.name;
    challengeGroupsChallengeBreadcrumb[2].link = `/challenge-groups/${groupId}/challenges`;

    const isChallengeGA = challenge.site === 2;

    const baseUrl = config[isChallengeGA ? 'GA_WEB_URL' : `IX_WEB_URL`];

    const redirectUrl = encodeURIComponent(`/challenges/${id}?previewKey=${challenge.previewKey}`);
    const previewAtutoLoginUrl = `${baseUrl}/autologin?token=${encodedJwt}&redirect=${redirectUrl}`;

    const previewUrl = `${baseUrl}/challenges/${id}?previewKey=${challenge.previewKey}`;

    const hasChallengeCrudPermissions = useHasChallengeCrudPermissions(challengeGroup, challenge);
    const isLeadOrSuperAdmin = userID === challenge.challengeLeadID || isSuperAdmin;

    const isPastPitch =
        dayjs(challenge.pitchPresentationDate).valueOf() <= getStartOfDayTimestamp();

    return (
        <>
            <DataCheck
                dataExists={!isEmpty(challenge) && !isFetchingSubmissions}
                isFetching={isFetching || isFetchingSubmissions}
                error={fetchError || fetchSubmissionsError}
            >
                <Breadcrumb
                    items={challengeGroupsChallengeBreadcrumb}
                    title={challenge.name || '-'}
                />
                <Title>
                    {challenge.name || 'Challenge Details'}
                    {!!challenge.assessmentsCompleteSetOn && (
                        <span className="complete-badge">
                            <i className={`icon fal fa-fw fa-check`} />
                            <span className="complete-caption">
                                Assessments
                                <br />
                                complete
                            </span>
                        </span>
                    )}
                </Title>
                <ButtonRow alignment="left" className="wrap">
                    <a
                        className="button green"
                        target="_blank"
                        rel="noreferrer"
                        href={previewAtutoLoginUrl}
                    >
                        <i className={`icon far fa-fw fa-eye left`}></i>
                        <span>Preview</span>
                    </a>
                    <ActionButton
                        color="blue"
                        icon="copy"
                        onClick={() => copyPreviewToCliboard(previewUrl)}
                    >
                        {copyPreviewSuccess
                            ? 'Preview link copied'
                            : copyPreviewFailure
                            ? 'Preview copy error'
                            : 'Copy preview link'}
                    </ActionButton>
                    {hasChallengeCrudPermissions && (
                        <LinkButton
                            color="yellow"
                            icon="pencil"
                            href={`/challenge-groups/${groupId}/challenges/${id}/edit`}
                        >
                            Edit
                        </LinkButton>
                    )}
                    <ActionButton
                        color="blue"
                        icon="copy"
                        onClick={() =>
                            copyToClipboard(
                                `${window.location.protocol}//${window.location.hostname}${window.location.pathname}`,
                            )
                        }
                    >
                        {copySuccess ? 'Link copied' : copyFailure ? 'Copy error' : 'Copy Link'}
                    </ActionButton>

                    <LinkButton
                        color="grey"
                        icon="search"
                        href={`/challenge-groups/${groupId}/application-summary/${id}`}
                    >
                        View submissions summary
                    </LinkButton>

                    {shouldShowMarkAsReadyForAssessment && isLeadOrSuperAdmin && (
                        <ActionButton
                            color="green"
                            icon="check"
                            onClick={() => setShowMarkToBeAssessedModal(true)}
                        >
                            Mark challenge as Ready for Assessment
                        </ActionButton>
                    )}

                    {!isPastPitch && shouldShowMarkAsAssessmentComplete && isSuperAdmin && (
                        <ActionButton
                            color="red"
                            icon="undo"
                            onClick={() => setShowResetAssessmentStatusModal(true)}
                        >
                            Reset Assessment Status
                        </ActionButton>
                    )}

                    {shouldShowMarkAsAssessmentComplete && isLeadOrSuperAdmin && (
                        <ActionButton
                            color="green"
                            icon="check"
                            onClick={() => setShowMarkToBeCompletedModal(true)}
                        >
                            Mark Challenge as Assessment Complete
                        </ActionButton>
                    )}

                    {!isPastPitch && !!challenge.assessmentsCompleteSetOn && isSuperAdmin && (
                        <ActionButton
                            color="red"
                            icon="undo"
                            onClick={() => setShowMarkToBeAssessedModal(true)}
                        >
                            Reopen Assessments
                        </ActionButton>
                    )}

                    {!challenge.assessmentsCompleteSetOn && hasChallengeCrudPermissions ? (
                        <>
                            <ActionButton
                                color="blue"
                                icon="star"
                                onClick={() => setShowEditScoreWeightingModal(true)}
                            >
                                Edit score weighting
                            </ActionButton>
                            <ActionButton
                                color="green"
                                icon="users"
                                onClick={() => setShowEditLeadModal(true)}
                            >
                                Update challenge lead
                            </ActionButton>
                        </>
                    ) : null}

                    {isLeadOrSuperAdmin && !isChallengeStarted && !isChallengeEnded ? (
                        <ActionButton
                            color="green"
                            icon="upload"
                            onClick={() => setShowPublishChallengeModal(true)}
                        >
                            Publish
                        </ActionButton>
                    ) : isLeadOrSuperAdmin &&
                      isChallengeStarted &&
                      !isChallengeEnded &&
                      !submissions.length ? (
                        <ActionButton
                            color="red"
                            icon="times"
                            onClick={() => setShowUnPublishChallengeModal(true)}
                        >
                            Unpublish
                        </ActionButton>
                    ) : null}
                </ButtonRow>
                <ContentBlock>
                    <ContentRow>
                        <ContentItem label="Name">
                            <p>{challenge.name || '-'}</p>
                        </ContentItem>
                        <ContentItem label="Challenge lead">
                            <p>
                                {!isEmpty(leadUser)
                                    ? `${leadUser?.firstName} ${leadUser?.lastName}`
                                    : '-'}
                            </p>
                        </ContentItem>
                    </ContentRow>
                    <ContentRow>
                        <ContentItem label="Tags">
                            <p>
                                {' '}
                                {Array.isArray(challenge.challengeTags)
                                    ? challenge.challengeTags.map(({ name }) => name).join(', ')
                                    : '-'}
                            </p>
                        </ContentItem>
                        <ContentItem label="Business tags">
                            <p>
                                {Array.isArray(challenge.businessTags)
                                    ? challenge.businessTags.map(({ name }) => name).join(', ')
                                    : '-'}
                            </p>
                        </ContentItem>
                    </ContentRow>
                    <ContentRow>
                        <ContentItem label="Workshop date">
                            <p>
                                {!!challenge.startDate
                                    ? dayjs(challenge.workshopDate).format('DD/MM/YYYY')
                                    : '-'}
                            </p>
                        </ContentItem>
                        <ContentItem label="Workshop admins">
                            <p>
                                {Array.isArray(challenge.workshopAdminIDs)
                                    ? challenge.workshopAdminIDs
                                          .filter(id => !!workshopAdmins[id])
                                          .map(
                                              id =>
                                                  `${workshopAdmins[id].firstName} ${workshopAdmins[id].lastName}`,
                                          )
                                          .join(', ')
                                    : '-'}
                            </p>
                        </ContentItem>
                    </ContentRow>
                    <ContentRow>
                        <ContentItem label="Challenge start">
                            <p>
                                {!!challenge.startDate
                                    ? dayjs(challenge.startDate).format('DD/MM/YYYY')
                                    : '-'}
                            </p>
                        </ContentItem>
                        <ContentItem label="Challenge end">
                            <p>
                                {!!challenge.endDate
                                    ? dayjs(challenge.endDate).format('DD/MM/YYYY')
                                    : '-'}
                            </p>
                        </ContentItem>
                    </ContentRow>
                    <ContentRow>
                        <ContentItem label="Grace period">
                            <p> {challenge.gracePeriod}</p>
                        </ContentItem>
                        <ContentItem label="Challenge Stage">
                            <p> {challengeStageValues[`${challenge.challengeStage}`] || '-'}</p>
                        </ContentItem>
                    </ContentRow>
                    <ContentRow>
                        <ContentItem label="Assessment start">
                            <p>
                                {!!challenge.assessmentStartDate
                                    ? dayjs(challenge.assessmentStartDate).format('DD/MM/YYYY')
                                    : '-'}
                            </p>
                        </ContentItem>
                        <ContentItem label="Response date">
                            <p>
                                {!!challenge.responseDate
                                    ? dayjs(challenge.responseDate).format('DD/MM/YYYY')
                                    : '-'}
                            </p>
                        </ContentItem>
                    </ContentRow>
                    <ContentRow>
                        <ContentItem label="Assessment admins">
                            <p>
                                {Array.isArray(challenge.assessorIDs)
                                    ? challenge.assessorIDs
                                          .filter(id => !!assessors[id])
                                          .map(
                                              id =>
                                                  `${assessors[id].firstName} ${assessors[id].lastName}`,
                                          )
                                          .join(', ')
                                    : '-'}
                            </p>
                        </ContentItem>
                    </ContentRow>
                    <ContentRow>
                        <ContentItem label="Pitch presentation date">
                            <p>
                                {!!challenge.pitchPresentationDate
                                    ? dayjs(challenge.pitchPresentationDate).format('DD/MM/YYYY')
                                    : '-'}
                            </p>
                        </ContentItem>
                        <ContentItem label="Pitch admins">
                            <p>
                                {Array.isArray(challenge.pitchPresentationAdminIDs)
                                    ? challenge.pitchPresentationAdminIDs
                                          .filter(id => !!pitchPresentationAdmins[id])
                                          .map(
                                              id =>
                                                  `${pitchPresentationAdmins[id].firstName} ${pitchPresentationAdmins[id].lastName}`,
                                          )
                                          .join(', ')
                                    : '-'}
                            </p>
                        </ContentItem>
                    </ContentRow>
                    <ContentRow>
                        <ContentItem label="Background">
                            <p>{challenge.background || '-'}</p>
                        </ContentItem>
                    </ContentRow>
                    <ContentRow>
                        <ContentItem label="Partner logo">
                            <p>
                                {' '}
                                {!!challenge.partnerLogoS3Key
                                    ? getS3Filename(challenge.partnerLogoS3Key)
                                    : '-'}
                            </p>
                        </ContentItem>
                        <ContentItem label="Partner logo second">
                            <p>
                                {!!challenge.partnerSecondLogoS3Key
                                    ? getS3Filename(challenge.partnerSecondLogoS3Key)
                                    : '-'}
                            </p>
                        </ContentItem>
                    </ContentRow>
                    <ContentRow>
                        <ContentItem label="Partner logo third">
                            <p>
                                {' '}
                                {!!challenge.partnerThirdLogoS3Key
                                    ? getS3Filename(challenge.partnerThirdLogoS3Key)
                                    : '-'}
                            </p>
                        </ContentItem>
                        <ContentItem label="Thumbnail">
                            <p>
                                {!!challenge.imageS3Key ? getS3Filename(challenge.imageS3Key) : '-'}
                            </p>
                        </ContentItem>
                    </ContentRow>
                    <ContentRow>
                        <ContentItem label="Summary image 1">
                            <p>
                                {' '}
                                {!!challenge.summaryImage1S3Key
                                    ? getS3Filename(challenge.summaryImage1S3Key)
                                    : '-'}
                            </p>
                        </ContentItem>
                        <ContentItem label="Summary image 2">
                            <p>
                                {!!challenge.summaryImage2S3Key
                                    ? getS3Filename(challenge.summaryImage2S3Key)
                                    : '-'}
                            </p>
                        </ContentItem>
                    </ContentRow>
                    {!challenge.descriptionSections && (
                        <ContentRow>
                            <ContentItem label="Background">
                                <p>{challenge.background || '-'}</p>
                            </ContentItem>
                        </ContentRow>
                    )}
                </ContentBlock>
                {!!challenge.applicationSnapshot?.length && (
                    <>
                        <Title>Applications Snapshot</Title>
                        <ContentBlock>
                            <ul>
                                {challenge.applicationSnapshot
                                    .slice()
                                    .sort((a, b) => b.percentComplete - a.percentComplete)
                                    .map(summaryItem => (
                                        <li key={summaryItem.percentComplete}>
                                            {summaryItem.count} x {summaryItem.percentComplete}%
                                            completed
                                        </li>
                                    ))}
                            </ul>
                        </ContentBlock>
                    </>
                )}

                {!!challenge.descriptionSections && (
                    <>
                        <Title>Description</Title>
                        <ContentBlock>
                            {challenge.descriptionSections
                                .slice()
                                .sort((a, b) => a.sort - b.sort)
                                .map((section, i) => {
                                    const SpecificComponent = descriptionComponents[section.type];
                                    const data =
                                        section.type === DescriptionSectionTypes.Accordion
                                            ? section
                                            : section.contents[0];
                                    return (
                                        <ContentRow key={i}>
                                            <ContentItem label={section.title || ''}>
                                                {/* @ts-ignore */}
                                                <SpecificComponent data={data} />
                                            </ContentItem>
                                        </ContentRow>
                                    );
                                })}
                        </ContentBlock>
                    </>
                )}
                {showEditScoreWeightingModal && (
                    <EditChallengeScoreWeightingModal
                        challenge={challenge}
                        setShowEditScoreWeightingModal={setShowEditScoreWeightingModal}
                    />
                )}
                {showEditLeadModal && (
                    <EditChallengeLeadModal
                        challenge={challenge}
                        setShowEditLeadModal={setShowEditLeadModal}
                    />
                )}
                {showMarkToBeAssessedModal && (
                    <MarkToBeAssessedModal
                        groupId={groupId}
                        challenge={challenge}
                        setShowMarkToBeAssessedModal={setShowMarkToBeAssessedModal}
                    />
                )}

                {showResetAssessmentStatusModal && (
                    <ResetAssessmentStatusModal
                        groupId={groupId}
                        challenge={challenge}
                        setShowModal={setShowResetAssessmentStatusModal}
                    />
                )}

                {showMarkToBeCompletedModal && (
                    <MarkToBeCompletedModal
                        groupId={groupId}
                        challenge={challenge}
                        setShowMarkToBeCompletedModal={setShowMarkToBeCompletedModal}
                    />
                )}

                {showPublishChallengeModal && (
                    <PublishChallengeModal
                        challengeID={challenge.id}
                        revert={false}
                        setShowModal={setShowPublishChallengeModal}
                    />
                )}

                {showUnPublishChallengeModal && (
                    <PublishChallengeModal
                        challengeID={challenge.id}
                        revert={true}
                        setShowModal={setShowUnPublishChallengeModal}
                    />
                )}

                <Title>Submissions</Title>
                <ContentBlock>
                    <ChallengeSubmissionsTable
                        id={challenge.id}
                        submissions={submissions}
                        isFetching={isFetchingSubmissions}
                        fetchError={fetchSubmissionsError}
                    />
                </ContentBlock>
            </DataCheck>
        </>
    );
};

const getStartOfDayTimestamp = () => {
    const date = new Date();
    date.setHours(0, 0, 0, 0);

    return date.getTime();
};

export default ChallengeGroupChallengeComponent;
